import LegalPageLayout, { Sec, SubSec, List } from "@/components/legal/LegalPageLayout";
import { LEGAL_DOCUMENTS } from "@/lib/legalDocuments";

export default function Privacy() {
  return (
    <LegalPageLayout
      document={LEGAL_DOCUMENTS.privacy}
      seoDescription="Privacy Policy for Hannah Howard Funding — how we collect, use, disclose, retain, and safeguard personal information."
    >
      <Sec n="1" title="Introduction">
        <p>Hannah Howard Funding ("Hannah Howard Funding," "Company," "we," "us," or "our") respects your privacy and is committed to protecting the personal information entrusted to us.</p>
        <p>This Privacy Policy explains how we collect, use, disclose, retain, and safeguard personal information when you:</p>
        <List items={[
          "visit our website;",
          "submit an inquiry or funding application;",
          "communicate with us by phone, email, SMS, or other electronic means;",
          "upload documents;",
          "schedule appointments;",
          "or otherwise interact with our products and services (collectively, the \"Services\").",
        ]}/>
        <p>By accessing or using the Services, you acknowledge that you have read and understand this Privacy Policy.</p>
      </Sec>

      <Sec n="2" title="Scope of this Policy">
        <p>This Privacy Policy applies to personal information collected through the Services.</p>
        <p>It does not apply to:</p>
        <List items={[
          "third-party websites;",
          "third-party applications;",
          "social media platforms;",
          "external payment providers;",
          "brokerage systems;",
          "title company systems; or",
          "other third-party services that operate under their own privacy policies.",
        ]}/>
        <p>We encourage you to review the privacy policies of those third parties before providing information to them.</p>
      </Sec>

      <Sec n="3" title="Categories of Information We Collect">
        <p>Depending on your interaction with the Services, we may collect the following categories of information.</p>

        <SubSec title="A. Personal Identification Information">
          <p>This may include:</p>
          <List items={[
            "full legal name;",
            "mailing address;",
            "email address;",
            "telephone number;",
            "date of birth (where requested);",
            "driver's license or other government-issued identification;",
            "photograph contained within identification documents.",
          ]}/>
        </SubSec>

        <SubSec title="B. Professional Information">
          <p>Including:</p>
          <List items={[
            "real estate license number;",
            "licensing state;",
            "brokerage affiliation;",
            "managing broker information;",
            "brokerage contact information;",
            "professional title;",
            "business address;",
            "professional email address.",
          ]}/>
        </SubSec>

        <SubSec title="C. Transaction Information">
          <p>Including:</p>
          <List items={[
            "property address;",
            "anticipated closing date;",
            "purchase price;",
            "estimated commission;",
            "requested advance amount;",
            "commission split;",
            "listing or buyer representation information;",
            "transaction status;",
            "escrow or settlement information;",
            "title company information;",
            "broker verification information.",
          ]}/>
        </SubSec>

        <SubSec title="D. Financial Information">
          <p>Where applicable, we may collect information reasonably necessary to facilitate funding or repayment, including:</p>
          <List items={[
            "payment instructions;",
            "banking information;",
            "wire instructions;",
            "settlement information;",
            "repayment information;",
            "tax reporting information where required by law.",
          ]}/>
          <p>We generally do not obtain traditional consumer credit reports unless specifically disclosed and authorized where applicable.</p>
        </SubSec>

        <SubSec title="E. Documents You Provide">
          <p>Including:</p>
          <List items={[
            "purchase contracts;",
            "listing agreements;",
            "commission agreements;",
            "brokerage authorizations;",
            "settlement documents;",
            "government-issued identification;",
            "transaction-related correspondence;",
            "other documents voluntarily uploaded or provided.",
          ]}/>
        </SubSec>

        <SubSec title="F. Communications">
          <p>We collect records of communications with us, including:</p>
          <List items={[
            "emails;",
            "SMS messages;",
            "telephone calls (where permitted by law);",
            "voicemail;",
            "online contact forms;",
            "customer support requests;",
            "appointment scheduling communications.",
          ]}/>
        </SubSec>

        <SubSec title="G. Technical Information">
          <p>When you use the Services, certain technical information may be collected automatically, including:</p>
          <List items={[
            "IP address;",
            "browser type and version;",
            "operating system;",
            "device type;",
            "device identifiers;",
            "session identifiers;",
            "cookie identifiers;",
            "referring website;",
            "pages visited;",
            "date and time of access;",
            "approximate geographic location derived from IP address;",
            "diagnostic information;",
            "application timestamps;",
            "security logs.",
          ]}/>
        </SubSec>
      </Sec>

      <Sec n="4" title="Cookies and Similar Technologies">
        <p>We use cookies and similar technologies to:</p>
        <List items={[
          "operate the website;",
          "remember user preferences;",
          "maintain secure sessions;",
          "improve website performance;",
          "understand how visitors use our Services;",
          "detect technical issues;",
          "measure marketing effectiveness;",
          "improve the overall user experience.",
        ]}/>
        <p>Depending on the technologies implemented on the website, cookies may include:</p>
        <List items={[
          "strictly necessary cookies;",
          "functional cookies;",
          "analytics cookies;",
          "performance cookies; and",
          "advertising or marketing cookies.",
        ]}/>
        <p>You may manage certain cookie preferences through your browser settings or other consent tools made available on the website. Disabling cookies may affect the functionality of certain portions of the Services.</p>
      </Sec>

      <Sec n="5" title="How We Use Personal Information">
        <p>We may use personal information to:</p>
        <List items={[
          "evaluate commission advance applications;",
          "verify identity;",
          "verify licensing status;",
          "verify brokerage affiliation;",
          "verify transaction details;",
          "communicate with brokers, title companies, settlement agents, lenders, attorneys, and other authorized transaction participants;",
          "detect, investigate, and prevent fraud;",
          "protect the security of our Services;",
          "process funding requests;",
          "administer funded transactions;",
          "provide customer service;",
          "schedule appointments;",
          "respond to inquiries;",
          "comply with legal obligations;",
          "maintain internal business records;",
          "improve our Services;",
          "analyze website performance;",
          "develop new products and services;",
          "enforce our agreements;",
          "protect the rights, property, and safety of Hannah Howard Funding, our users, and others.",
        ]}/>
        <p>We process personal information only for legitimate business purposes and in accordance with applicable law.</p>
      </Sec>

      <Sec n="6" title="How We Share Information">
        <p>We do not sell your personal information for monetary consideration.</p>
        <p>We may disclose personal information when reasonably necessary to operate our business, process applications, complete transactions, comply with legal obligations, or protect legitimate business interests.</p>
        <p>Information may be shared with the following categories of recipients:</p>

        <SubSec title="A. Brokerage and Transaction Participants">
          <p>Including:</p>
          <List items={[
            "your real estate brokerage;",
            "managing broker;",
            "transaction coordinator;",
            "title company;",
            "escrow company;",
            "settlement agent;",
            "closing attorney;",
            "lender;",
            "or other authorized participants involved in the transaction.",
          ]}/>
          <p>Such disclosures are made only for purposes relating to underwriting, verification, funding administration, settlement coordination, repayment, fraud prevention, or compliance.</p>
        </SubSec>

        <SubSec title="B. Service Providers">
          <p>We may engage third-party vendors that assist in operating our business.</p>
          <p>These providers may include:</p>
          <List items={[
            "cloud hosting providers;",
            "payment processors;",
            "banking partners;",
            "identity verification providers;",
            "fraud prevention providers;",
            "CRM platforms;",
            "document management providers;",
            "electronic signature providers;",
            "email service providers;",
            "SMS providers;",
            "scheduling providers;",
            "customer support platforms;",
            "website analytics providers;",
            "cybersecurity providers;",
            "professional advisors.",
          ]}/>
          <p>Service providers are authorized to access personal information only as necessary to perform services on our behalf and are expected to protect such information in accordance with applicable contractual obligations.</p>
        </SubSec>

        <SubSec title="C. Legal Compliance">
          <p>We may disclose information where reasonably necessary to:</p>
          <List items={[
            "comply with applicable law;",
            "comply with subpoenas;",
            "comply with court orders;",
            "comply with lawful governmental requests;",
            "cooperate with regulators;",
            "protect our legal rights;",
            "investigate fraud;",
            "enforce our agreements;",
            "protect public safety.",
          ]}/>
        </SubSec>

        <SubSec title="D. Corporate Transactions">
          <p>If Hannah Howard Funding undergoes a merger, acquisition, financing, restructuring, bankruptcy, reorganization, or sale of all or substantially all of its assets, personal information may be transferred as part of that transaction, subject to applicable law.</p>
        </SubSec>
      </Sec>

      <Sec n="7" title="Communications and TCPA Consent">
        <p>If you voluntarily provide your telephone number or otherwise consent through our application or website, you acknowledge that Hannah Howard Funding and its authorized service providers may contact you regarding your inquiry, application, funding transaction, account, servicing, or customer support.</p>
        <p>Communications may include:</p>
        <List items={[
          "telephone calls;",
          "SMS or MMS text messages;",
          "emails;",
          "voicemail messages; and",
          "other electronic communications.",
        ]}/>
        <p>Where applicable and with your consent, communications may be delivered using automated telephone dialing systems, prerecorded or artificial voice technology, or similar technologies.</p>
        <p>Consent to receive marketing communications is not a condition of applying for or receiving funding.</p>
        <p>You may opt out of marketing SMS communications at any time by following the instructions provided within the message, including replying STOP where applicable.</p>
        <p>Separate TCPA disclosures presented during the application process supplement this Privacy Policy.</p>
      </Sec>

      <Sec n="8" title="Data Retention">
        <p>We retain personal information only for as long as reasonably necessary to:</p>
        <List items={[
          "provide the Services;",
          "evaluate applications;",
          "administer funded transactions;",
          "comply with legal obligations;",
          "comply with tax and accounting requirements;",
          "resolve disputes;",
          "investigate fraud;",
          "enforce agreements;",
          "maintain appropriate business records.",
        ]}/>
        <p>Retention periods vary depending upon the type of information, applicable legal requirements, contractual obligations, and legitimate business needs.</p>
        <p>Following expiration of applicable retention periods, information may be securely deleted, anonymized, or archived in accordance with our record retention practices.</p>
      </Sec>

      <Sec n="9" title="Information Security">
        <p>We maintain administrative, technical, and physical safeguards designed to protect personal information against unauthorized access, disclosure, alteration, or destruction.</p>
        <p>Depending upon the systems utilized, safeguards may include:</p>
        <List items={[
          "encrypted transmission technologies;",
          "encrypted storage where appropriate;",
          "authentication controls;",
          "role-based access restrictions;",
          "multi-factor authentication for administrative access where implemented;",
          "system monitoring;",
          "audit logging;",
          "employee confidentiality obligations;",
          "vendor security reviews;",
          "routine software maintenance.",
        ]}/>
        <p>Although we employ reasonable security measures, no method of transmitting or storing electronic information can be guaranteed to be completely secure.</p>
        <p>Accordingly, we cannot guarantee absolute security.</p>
      </Sec>

      <Sec n="10" title="Your Privacy Choices">
        <p>Subject to applicable law, you may have the right to:</p>
        <List items={[
          "request access to personal information we maintain about you;",
          "request correction of inaccurate information;",
          "request deletion of certain personal information;",
          "withdraw consent where processing is based upon consent;",
          "opt out of marketing communications;",
          "request information regarding certain disclosures.",
        ]}/>
        <p>We may need to verify your identity before processing requests.</p>
        <p>Certain information may be retained where required by law, contractual obligation, fraud prevention, dispute resolution, or other legitimate business purposes.</p>
        <p>Residents of certain states may possess additional privacy rights under applicable law.</p>
        <p>
          To submit a privacy request, please use our{" "}
          <a href="/privacy-request" className="underline hover:text-[#2A1C14]">
            Privacy Request Form
          </a>{" "}
          or contact us using the information below.
        </p>
      </Sec>

      <Sec n="11" title="Children's Privacy">
        <p>The Services are intended solely for adults involved in professional real estate transactions.</p>
        <p>We do not knowingly collect personal information from individuals under thirteen (13) years of age.</p>
        <p>If we become aware that personal information has been collected from a child in violation of applicable law, we will take reasonable steps to delete that information.</p>
      </Sec>

      <Sec n="12" title="International Users">
        <p>The Services are intended primarily for individuals located within the United States.</p>
        <p>If you access the Services from outside the United States, you acknowledge that your information may be transferred to, processed, and stored within the United States, where privacy laws may differ from those in your jurisdiction.</p>
      </Sec>

      <Sec n="13" title="Changes to this Privacy Policy">
        <p>We may revise this Privacy Policy from time to time.</p>
        <p>The "Last Updated" date appearing at the beginning of this Policy reflects the effective date of the most recent revision.</p>
        <p>Material changes will become effective upon posting unless otherwise required by applicable law.</p>
        <p>Continued use of the Services following publication of revised policies constitutes acknowledgment of the updated Privacy Policy.</p>
      </Sec>

      <Sec n="14" title="Contact Information">
        <p>Questions regarding this Privacy Policy or requests concerning your personal information may be directed to:</p>
        <p>
          Hannah Howard Funding<br />
          Email: hello@hannahhowardfunding.com<br />
          Phone: (870) 919-9520<br />
          Mailing Address: 495 Brickell Ave Miami, FL 33131
        </p>
      </Sec>
    </LegalPageLayout>
  );
}
